<script>
    import { createEventDispatcher } from "svelte";
    import { Section } from "../../lib/ui";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
    import {
        WAIT_SCREENS,
        MAIN_SCREEN_SLOTS,
        STAT_BAR_SLOTS,
        BUTTON_ACTIONS,
        newMainButton,
    } from "./gameSlots.js";

    export let bot;

    export let mainTitle = "";
    export let mainSubtitle = "";
    export let startLabel = "";
    /** 타이틀을 비웠을 때 게임이 대신 쓰는 값 — 무엇이 나올지 자리표시자로 보여준다. */
    export let botTitle = "";

    /**
     * 메인 화면 구성(서버 MAIN 컬럼). 문구 3개와 달리 **한 덩어리 JSON** 이다 —
     * 항목이 늘 때마다 컬럼을 늘리지 않으려고.
     */
    export let main = {};

    /** 슬롯키 → assetId(ref). [기본 이미지] 탭과 **같은 맵**이다(슬롯 키만 다르다). */
    export let assets = {};
    /** 슬롯키 → 현재 버전 URL. 썸네일용(저장하지 않는다). */
    export let urls = {};
    /** assetId → URL. 하단 버튼 아이콘 썸네일용(키 체계가 슬롯과 다르다). */
    export let iconUrls = {};

    const dispatch = createEventDispatcher();

    /** 문구·구성은 한 번에 올린다 — 같은 UPDATE 로 나가므로 나눌 이유가 없다. */
    const emit = (patch = {}) =>
        dispatch("change", {
            mainTitle,
            mainSubtitle,
            startLabel,
            main: { ...normalized, ...patch },
        });

    // 서버는 **저작자가 정한 값만** 내려준다 → 화면에 그릴 기본값은 여기서 채운다.
    // ⚠️ 런타임 기본값(config.ts resolveConfig)과 같아야 한다. 다르면 "빌더에 보이는 것과
    //    게임에 나오는 것이 다르다"가 된다.
    // ⚠️⚠️ **원본을 먼저 펼친다.** 이 탭이 저장할 때 `main` 을 통째로 올려보내는데,
    //    여기서 아는 키만 추리면 다른 탭이 쓰는 키([디자인] 탭의 `theme`)가 조용히 지워진다.
    $: normalized = {
        ...(main || {}),
        backUrl: main?.backUrl ?? "",
        bootText: main?.bootText ?? "",
        showBootBar: main?.showBootBar ?? true,
        introText: main?.introText ?? "",
        introShowMap: main?.introShowMap ?? true,
        showLevel: main?.showLevel ?? true,
        showCurrency: main?.showCurrency ?? true,
        showRanking: main?.showRanking ?? true,
        currencyLabel: main?.currencyLabel ?? "",
        mapPrefix: main?.mapPrefix ?? "",
        buttons: Array.isArray(main?.buttons) ? main.buttons : [],
    };

    const setFlag = (key, value) => emit({ [key]: value });
    const setText = (key, value) => emit({ [key]: value });

    /* ── 하단 버튼 ─────────────────────────────────────────────────────── */

    const setButtons = (buttons) => emit({ buttons });

    const addButton = () => setButtons([...normalized.buttons, newMainButton()]);
    const removeButton = (index) =>
        setButtons(normalized.buttons.filter((_, i) => i !== index));

    const updateButton = (index, patch) =>
        setButtons(normalized.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));

    const moveButton = (index, dir) => {
        const to = index + dir;
        if (to < 0 || to >= normalized.buttons.length) return;
        const next = [...normalized.buttons];
        [next[index], next[to]] = [next[to], next[index]];
        setButtons(next);
    };

    /* ── 그림 고르기 ───────────────────────────────────────────────────── */

    let pickerOpen = false;
    /** { kind: 'slot', key } | { kind: 'button', index } */
    let pickerTarget = null;

    const openSlotPicker = (key) => {
        pickerTarget = { kind: "slot", key };
        pickerOpen = true;
    };
    const openButtonPicker = (index) => {
        pickerTarget = { kind: "button", index };
        pickerOpen = true;
    };

    const onPick = (e) => {
        const { assetId, url } = e.detail;
        pickerOpen = false;
        const target = pickerTarget;
        pickerTarget = null;
        if (!target) return;

        if (target.kind === "slot") {
            dispatch("assets", {
                assets: { ...assets, [target.key]: assetId },
                urls: { ...urls, [target.key]: url },
            });
            return;
        }
        // 버튼 아이콘은 슬롯이 아니라 버튼 객체 안에 담는다 —
        // 슬롯 맵에 쌓으면 버튼을 지워도 키가 남아 고아가 된다(캐릭터 그림과 같은 이유).
        dispatch("assetUrl", { assetId, url });
        updateButton(target.index, { iconAssetId: assetId });
    };

    // 슬롯을 비우면 런타임이 기본 모습으로 돌아간다 — 값을 지우는 게 곧 "기본으로"다.
    const clearSlot = (key) => {
        const nextAssets = { ...assets };
        const nextUrls = { ...urls };
        delete nextAssets[key];
        delete nextUrls[key];
        dispatch("assets", { assets: nextAssets, urls: nextUrls });
    };

    const actionOf = (b) => BUTTON_ACTIONS.find((a) => a.value === b.action);

    // 그림 고르기 창이 "지금 뭐가 골라져 있는지" 표시하려면 대상에 따라 다른 자리를 봐야 한다.
    $: currentPickerAssetId =
        pickerTarget?.kind === "slot"
            ? (assets[pickerTarget.key] ?? null)
            : pickerTarget?.kind === "button"
              ? (normalized.buttons[pickerTarget.index]?.iconAssetId ?? null)
              : null;
</script>

<!-- 기다리는 동안 보이는 화면 둘 — 플레이어가 **가장 먼저** 보는 자리라 맨 위에 둔다.
     모양이 같아서(배경+문구+토글) 한 묶음(WAIT_SCREENS)을 돌며 섹션을 두 개 그린다. -->
{#each WAIT_SCREENS as sc (sc.slot.key)}
    <Section title={sc.title} description={sc.desc}>
        <div class="tw-flex tw-flex-wrap tw-gap-5">
            <div class="tw-flex tw-w-[132px] tw-shrink-0 tw-flex-col tw-items-center tw-gap-1">
                <button
                    type="button"
                    class="slot-thumb {urls[sc.slot.key]
                        ? 'tw-border-border'
                        : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                    on:click={() => openSlotPicker(sc.slot.key)}
                >
                    {#if urls[sc.slot.key]}
                        <!-- ⚠️ crossorigin 필수: 없으면 브라우저가 Origin 없는 응답을 캐시에 넣고,
                             뒤이은 게임(Phaser XHR)이 그 캐시를 재사용해 CORS 로 막힌다. -->
                        <img
                            crossorigin="anonymous"
                            class="tw-max-h-full tw-max-w-full tw-object-contain"
                            src={urls[sc.slot.key]}
                            alt=""
                        />
                    {:else}
                        <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                    {/if}
                </button>
                <div class="tw-text-[13px] tw-font-semibold tw-text-foreground">{sc.slot.label}</div>
                <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                    {sc.slot.hint}
                </div>
                {#if urls[sc.slot.key]}
                    <button
                        type="button"
                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                        on:click={() => clearSlot(sc.slot.key)}
                    >
                        비우기
                    </button>
                {/if}
            </div>

            <div class="tw-flex tw-min-w-[260px] tw-flex-1 tw-flex-col tw-gap-4">
                <label class="tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">문구</span>
                    <input
                        class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                        type="text"
                        maxlength="50"
                        placeholder={sc.textPlaceholder}
                        value={normalized[sc.textKey]}
                        on:change={(e) => setText(sc.textKey, e.target.value.trim())}
                    />
                    <span class="tw-text-xs tw-text-muted-foreground">{sc.textHint}</span>
                </label>

                <label class="tw-flex tw-items-start tw-gap-2 tw-text-[13px] tw-text-foreground">
                    <input
                        type="checkbox"
                        checked={normalized[sc.flagKey]}
                        on:change={(e) => setFlag(sc.flagKey, e.target.checked)}
                    />
                    <span class="tw-flex tw-flex-col tw-gap-1">
                        {sc.flagLabel}
                        <span class="tw-text-xs tw-text-muted-foreground">{sc.flagHint}</span>
                    </span>
                </label>
            </div>
        </div>
    </Section>
{/each}

<!-- 화면에 까는 그림 -->
<Section
    title="화면"
    description="메인 화면에 까는 그림입니다. 플레이 중 배경과 다른 슬롯이라 따로 넣습니다 — 플레이 배경은 세로로 이어지게 그려야 하지만 메인은 한 장짜리 일러스트입니다."
>
    <div class="tw-flex tw-flex-wrap tw-gap-4">
        {#each MAIN_SCREEN_SLOTS as slot (slot.key)}
            <div class="tw-flex tw-w-[132px] tw-flex-col tw-items-center tw-gap-1">
                <button
                    type="button"
                    class="slot-thumb {urls[slot.key]
                        ? 'tw-border-border'
                        : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                    on:click={() => openSlotPicker(slot.key)}
                >
                    {#if urls[slot.key]}
                        <!-- ⚠️ crossorigin 필수: 없으면 브라우저가 Origin 없는 응답을 캐시에 넣고,
                             뒤이은 게임(Phaser XHR)이 그 캐시를 재사용해 CORS 로 막힌다. -->
                        <img
                            crossorigin="anonymous"
                            class="tw-max-h-full tw-max-w-full tw-object-contain"
                            src={urls[slot.key]}
                            alt=""
                        />
                    {:else}
                        <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                    {/if}
                </button>
                <div class="tw-text-[13px] tw-font-semibold tw-text-foreground">{slot.label}</div>
                <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                    {slot.hint}
                </div>
                {#if urls[slot.key]}
                    <button
                        type="button"
                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                        on:click={() => clearSlot(slot.key)}
                    >
                        비우기
                    </button>
                {/if}
            </div>
        {/each}
    </div>
</Section>

<!-- 문구 -->
<Section title="문구" description="메인 화면에 나오는 글자들입니다. (부팅·진입 화면 문구는 맨 위에서 따로 정합니다)">
    <div class="tw-flex tw-flex-col tw-gap-4">
        <label class="tw-flex tw-flex-col tw-gap-1.5">
            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">타이틀</span>
            <input
                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                maxlength="100"
                bind:value={mainTitle}
                on:change={() => emit()}
                placeholder={botTitle || "게임 이름"}
            />
            <span class="tw-text-xs tw-text-muted-foreground">
                비우면 프로젝트 이름({botTitle || "제목 없음"})이 나옵니다. 위에서 로고를 넣으면
                로고가 대신 나옵니다.
            </span>
        </label>

        <label class="tw-flex tw-flex-col tw-gap-1.5">
            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">부제</span>
            <input
                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                maxlength="200"
                bind:value={mainSubtitle}
                on:change={() => emit()}
                placeholder="한 줄 설명 (선택)"
            />
            <span class="tw-text-xs tw-text-muted-foreground">비우면 아무것도 그리지 않습니다.</span>
        </label>

        <label class="tw-flex tw-flex-col tw-gap-1.5">
            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">시작 버튼 문구</span>
            <input
                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                maxlength="50"
                bind:value={startLabel}
                on:change={() => emit()}
                placeholder="게임 시작"
            />
            <span class="tw-text-xs tw-text-muted-foreground">비우면 '게임 시작'으로 나옵니다.</span>
        </label>
    </div>
</Section>

<!-- 상단 -->
<Section
    title="상단"
    description="화면 맨 위 줄과 그 아래 상태 표시입니다. 끈 칸은 자리를 남기지 않고 사라집니다."
>
    <div class="tw-flex tw-flex-col tw-gap-4">
        <label class="tw-flex tw-flex-col tw-gap-1.5">
            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">뒤로 가기 주소</span>
            <input
                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                placeholder="https://… (비우면 버튼을 안 그립니다)"
                value={normalized.backUrl}
                on:change={(e) => setText("backUrl", e.target.value.trim())}
            />
            <span class="tw-text-xs tw-text-muted-foreground">
                게임이 단독 페이지로 열릴 수도 있어서, 돌아갈 곳을 정했을 때만 왼쪽 위에 버튼이 생깁니다.
            </span>
        </label>

        <div class="tw-flex tw-flex-col tw-gap-3">
            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">상태 표시</span>
            <!-- 켠 칸에만 아이콘 자리를 보여준다 — 끈 칸의 아이콘까지 깔리면 화면만 복잡해진다. -->
            {#each STAT_BAR_SLOTS as s (s.key)}
                <div class="tw-flex tw-items-start tw-gap-3">
                    <label class="tw-flex tw-h-[72px] tw-w-[150px] tw-shrink-0 tw-items-center tw-gap-2 tw-text-[13px] tw-text-foreground">
                        <input
                            type="checkbox"
                            checked={normalized[s.flag]}
                            on:change={(e) => setFlag(s.flag, e.target.checked)}
                        />
                        {s.label}
                    </label>

                    {#if normalized[s.flag]}
                        <button
                            type="button"
                            class="slot-thumb slot-thumb-icon {urls[s.key]
                                ? 'tw-border-border'
                                : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                            on:click={() => openSlotPicker(s.key)}
                        >
                            {#if urls[s.key]}
                                <img
                                    crossorigin="anonymous"
                                    class="tw-max-h-full tw-max-w-full tw-object-contain"
                                    src={urls[s.key]}
                                    alt=""
                                />
                            {:else}
                                <span class="tw-text-lg tw-text-muted-foreground">+</span>
                            {/if}
                        </button>
                        <div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1 tw-pt-1">
                            <span class="tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
                                {s.hint}
                            </span>
                            {#if urls[s.key]}
                                <button
                                    type="button"
                                    class="tw-w-fit tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-text-muted-foreground tw-underline"
                                    on:click={() => clearSlot(s.key)}
                                >
                                    비우기
                                </button>
                            {/if}
                        </div>
                    {/if}
                </div>
            {/each}
        </div>

        <div class="tw-flex tw-flex-wrap tw-gap-4">
            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">재화 이름</span>
                <input
                    class="tw-h-9 tw-w-[180px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                    type="text"
                    maxlength="10"
                    placeholder="코인"
                    value={normalized.currencyLabel}
                    on:change={(e) => setText("currencyLabel", e.target.value.trim())}
                />
                <span class="tw-text-xs tw-text-muted-foreground">
                    "{normalized.currencyLabel || "코인"}이 3 더 필요해요" 처럼 안내문에 쓰입니다.
                    아이콘은 [기본 이미지] 탭의 코인 그림입니다.
                </span>
            </label>

            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">맵 앞에 붙일 말</span>
                <input
                    class="tw-h-9 tw-w-[180px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                    type="text"
                    maxlength="10"
                    placeholder="(비우면 맵 이름만)"
                    value={normalized.mapPrefix}
                    on:change={(e) => setText("mapPrefix", e.target.value.trim())}
                />
                <span class="tw-text-xs tw-text-muted-foreground">
                    {normalized.mapPrefix
                        ? `맵 고르기에 "${normalized.mapPrefix} 1" 처럼 나오고, 맵 이름은 아랫줄로 갑니다.`
                        : "맵 고르기에 맵 이름이 그대로 나옵니다."}
                </span>
            </label>
        </div>
    </div>
</Section>

<!-- 하단 버튼 -->
<Section
    title="하단 버튼"
    description="화면 맨 아래 줄에 놓이는 버튼입니다. 아이콘과 글자를 넣고, 눌렀을 때 할 일을 고릅니다."
>
    <p class="tw-mb-3 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        맵은 여기가 아니라 <strong class="tw-text-foreground">화면 위쪽에서 좌우로 넘겨</strong> 고릅니다.
        버튼을 하나도 안 만들면 [캐릭터] 버튼 하나가 자동으로 나옵니다 — 안 그러면 캐릭터를 고를 방법이 없어서입니다.
    </p>

    {#each normalized.buttons as b, i (b.id)}
        <div class="tw-mb-3 tw-rounded-lg tw-border tw-border-border tw-p-4">
            <div class="tw-flex tw-flex-wrap tw-items-start tw-gap-4">
                <div class="tw-flex tw-w-[92px] tw-flex-col tw-items-center tw-gap-1">
                    <button
                        type="button"
                        class="slot-thumb slot-thumb-sm {b.iconAssetId && iconUrls[b.iconAssetId]
                            ? 'tw-border-border'
                            : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                        on:click={() => openButtonPicker(i)}
                    >
                        {#if b.iconAssetId && iconUrls[b.iconAssetId]}
                            <img
                                crossorigin="anonymous"
                                class="tw-max-h-full tw-max-w-full tw-object-contain"
                                src={iconUrls[b.iconAssetId]}
                                alt=""
                            />
                        {:else}
                            <span class="tw-text-xl tw-text-muted-foreground">+</span>
                        {/if}
                    </button>
                    <span class="tw-text-[11px] tw-text-muted-foreground">아이콘</span>
                    {#if b.iconAssetId}
                        <button
                            type="button"
                            class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                            on:click={() => updateButton(i, { iconAssetId: null })}
                        >
                            비우기
                        </button>
                    {/if}
                </div>

                <div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3">
                    <div class="tw-flex tw-flex-wrap tw-gap-3">
                        <label class="tw-flex tw-min-w-[160px] tw-flex-1 tw-flex-col tw-gap-1.5">
                            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">버튼 글자</span>
                            <input
                                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                                type="text"
                                maxlength="20"
                                placeholder="예: 스킨상점"
                                value={b.label || ""}
                                on:change={(e) => updateButton(i, { label: e.target.value })}
                            />
                        </label>

                        <label class="tw-flex tw-flex-col tw-gap-1.5">
                            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">누르면</span>
                            <select
                                class="tw-h-9 tw-w-[180px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                                value={b.action}
                                on:change={(e) => updateButton(i, { action: e.target.value })}
                            >
                                {#each BUTTON_ACTIONS as a (a.value)}
                                    <option value={a.value}>{a.label}</option>
                                {/each}
                            </select>
                        </label>
                    </div>

                    <span class="tw-text-xs tw-text-muted-foreground">{actionOf(b)?.hint || ""}</span>

                    {#if b.action === "guide"}
                        <label class="tw-flex tw-flex-col tw-gap-1.5">
                            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">안내문</span>
                            <textarea
                                class="tw-min-h-[110px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-p-3 tw-text-sm tw-text-foreground"
                                maxlength="1000"
                                placeholder="예: 손가락으로 비행기를 움직여 적을 피하세요. 아래 버튼을 누르면 스킬이 나갑니다."
                                value={b.text || ""}
                                on:change={(e) => updateButton(i, { text: e.target.value })}
                            ></textarea>
                        </label>
                    {:else if b.action === "link"}
                        <label class="tw-flex tw-flex-col tw-gap-1.5">
                            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">열 주소</span>
                            <input
                                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                                type="text"
                                placeholder="https://…"
                                value={b.url || ""}
                                on:change={(e) => updateButton(i, { url: e.target.value.trim() })}
                            />
                            <span class="tw-text-xs tw-text-muted-foreground">새 창으로 열립니다.</span>
                        </label>
                    {/if}
                </div>

                <div class="tw-flex tw-gap-2">
                    <button type="button" class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground" title="왼쪽으로" on:click={() => moveButton(i, -1)}>←</button>
                    <button type="button" class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground" title="오른쪽으로" on:click={() => moveButton(i, 1)}>→</button>
                    <button type="button" class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-xs tw-text-muted-foreground hover:tw-border-destructive hover:tw-text-destructive" on:click={() => removeButton(i)}>삭제</button>
                </div>
            </div>
        </div>
    {/each}

    <button
        type="button"
        class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-dashed tw-border-border tw-bg-background tw-px-4 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground"
        on:click={addButton}
    >
        + 버튼 추가
    </button>

    {#if normalized.buttons.length > 0 && !normalized.buttons.some((b) => b.action === "characters")}
        <div class="tw-mt-3 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
            캐릭터를 고르는 버튼이 없습니다. 캐릭터를 여러 개 만들었다면 플레이어가 바꿀 방법이 없어요.
        </div>
    {/if}
</Section>

<ImagePicker
    bind:open={pickerOpen}
    chatId={bot.id}
    contextFolder={botAssetFolder(bot)}
    mode="single"
    currentAssetId={currentPickerAssetId}
    on:select={onPick}
/>

<style>
    /* 투명 PNG 가 대부분이라 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다.
       (토큰 색이 아니라 고정 색인 이유: 실제 게임 배경이 어둡기 때문. 테마와 무관하게 같아야 한다) */
    .slot-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 108px;
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }

    .slot-thumb:hover {
        border-color: hsl(var(--sb-primary));
    }

    /* 버튼 아이콘은 실제로도 작게 나온다 — 칸을 줄여 목록이 길어지지 않게. */
    .slot-thumb-sm {
        height: 72px;
    }

    /* 스탯바 아이콘은 토글 한 줄 안에 들어가야 해서 정사각 한 칸으로 고정한다. */
    .slot-thumb-icon {
        width: 72px;
        height: 72px;
        flex-shrink: 0;
    }
</style>
